/**
 * Trashcan Block Schema
 *
 * Container für soft-deleted Database- und Automation-Blocks.
 * Speichert Block-Snapshots als Daten (keine echten Children).
 *
 * @module schemas/trashcan-model
 */

import {
  BlockModel,
  BlockSchemaExtension,
  defineBlockSchema,
} from '@blocksuite/store';

// =============================================================================
// Types
// =============================================================================

/**
 * Ein gelöschtes Item im Trashcan
 */
export interface TrashcanItem {
  /** Original Block-ID */
  id: string;
  /** Block-Typ */
  blockType: 'database' | 'automation' | 'elster';
  /** Serialisierter Block (BlockSnapshot) */
  snapshot: unknown;
  /** Timestamp wann gelöscht wurde */
  deletedAt: number;
  /** Optional: Name des Blocks für Anzeige */
  displayName?: string;
  /** true when permanent delete also needs explicit owner-aware confirmation */
  protected?: boolean;
  /** Optional owner marker for protected deleted blocks */
  managedBy?: string;
  /** Sharded database identity: logical database id (database items only) */
  databaseId?: string;
  /** Sharded database identity: root system document id (database items only) */
  databaseRootDocId?: string;
}

/**
 * Trashcan Block Props
 */
export type TrashcanBlockProps = {
  /** Liste der gelöschten Items */
  items: TrashcanItem[];
};

// =============================================================================
// Model
// =============================================================================

export class TrashcanBlockModel extends BlockModel<TrashcanBlockProps> {
  /**
   * Anzahl der Items im Trashcan
   */
  get itemCount(): number {
    return this.props.items.length;
  }

  /**
   * Finde Item by ID
   */
  getItem(id: string): TrashcanItem | undefined {
    return this.props.items.find(item => item.id === id);
  }

  /**
   * Alle Database-Items
   */
  get databaseItems(): TrashcanItem[] {
    return this.props.items.filter(item => item.blockType === 'database');
  }

  /**
   * Alle Automation-Items
   */
  get automationItems(): TrashcanItem[] {
    return this.props.items.filter(item => item.blockType === 'automation');
  }
}

// =============================================================================
// Schema
// =============================================================================

export const TrashcanBlockSchema = defineBlockSchema({
  flavour: 'affine:trashcan',
  props: (): TrashcanBlockProps => ({
    items: [],
  }),
  metadata: {
    role: 'hub',
    version: 1,
    parent: ['@root'],
    children: [], // Keine Children - nur Daten!
  },
  toModel: () => new TrashcanBlockModel(),
});

// =============================================================================
// Extension
// =============================================================================

export const TrashcanBlockSchemaExtension =
  BlockSchemaExtension(TrashcanBlockSchema);
